/**
 * Customer-facing site / org labels — hide raw UUIDs where a human name exists.
 */

import type { TenantSiteModel } from '@shared/site/types/tenant-site-model';

const UUID_LIKE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function looksLikeUuid(value: string): boolean {
  return UUID_LIKE.test(value.trim());
}

export function resolveSiteBrandLabel(model: TenantSiteModel, fallbackSlug: string): string {
  const seoTitle = model.seo?.title?.trim();
  if (seoTitle && !looksLikeUuid(seoTitle)) {
    return seoTitle;
  }

  const homePage = model.pages.find((p) => p.slug === 'home' || p.navRole === 'home');
  const pageTitle = homePage?.title?.ko?.trim();
  if (pageTitle) return pageTitle;

  const slug = fallbackSlug.trim();
  if (slug && !looksLikeUuid(slug)) return slug;
  return '조직 Site';
}

export function resolveTenantOrgLabel(
  displayName: string | undefined,
  name: string | undefined,
  fallbackId: string
): string {
  const candidate = displayName?.trim() || name?.trim();
  if (candidate && !looksLikeUuid(candidate)) return candidate;

  const fallback = fallbackId.trim();
  if (fallback && !looksLikeUuid(fallback)) return fallback;
  return '조직';
}

/** Prefer human name; hide raw UUID ids in customer-facing UI. */
export function resolveTechnicalDisplayLabel(
  id: string | undefined | null,
  name: string | undefined | null,
  fallback: string
): string {
  const named = name?.trim();
  if (named && !looksLikeUuid(named)) return named;

  const raw = id?.trim();
  if (raw && !looksLikeUuid(raw)) return raw;

  return fallback;
}

/** User-facing label for account / member ids in modals and tables. */
export function formatUserDisplayLabel(
  userId: string | undefined | null,
  userName?: string | null
): string {
  return resolveTechnicalDisplayLabel(userId, userName, '사용자');
}
